import React, { useMemo, useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { DateRange } from "react-date-range";
import { es as esLocale } from "date-fns/locale";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { HiOutlineCalendarDays, HiOutlineClock, HiOutlineTrash } from "react-icons/hi2";
import { ChevronDown, Search, X } from "lucide-react";

function SearchableUserSelect({ users, value, onChange, label, placeholder }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const selectedUser = users.find((u) => u.correo_tecnico === value);
  const filtered = search.trim()
    ? users.filter((u) => u.nombre_tecnico?.toLowerCase().includes(search.toLowerCase()) || u.sucursal?.toLowerCase().includes(search.toLowerCase()))
    : users;
  useEffect(() => {
    const handleClickOutside = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  return (
    <div ref={containerRef} className="relative w-full">
      {label && <span className="mb-1.5 block text-sm font-medium text-[color:var(--text-secondary)]">{label}</span>}
      <button type="button" onClick={() => { setOpen((p) => !p); setTimeout(() => inputRef.current?.focus(), 0); }}
        className="flex w-full items-center justify-between rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-left text-sm text-slate-800 shadow-sm outline-none transition">
        {selectedUser ? <span className="truncate">{selectedUser.nombre_tecnico}</span> : <span className="text-slate-400">{placeholder || "Seleccionar..."}</span>}
        <span className="flex shrink-0 items-center gap-1 text-slate-400">
          {selectedUser && <X size={14} className="cursor-pointer hover:text-rose-500" onClick={(e) => { e.stopPropagation(); onChange(""); }} />}
          <ChevronDown size={14} className={`transition ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div className="absolute z-[60] mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2">
            <Search size={14} className="shrink-0 text-slate-400" />
            <input ref={inputRef} type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre o área..." className="w-full bg-transparent text-sm outline-none" />
            {search && <X size={14} className="shrink-0 cursor-pointer text-slate-400 hover:text-rose-500" onClick={() => setSearch("")} />}
          </div>
          <ul className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-3 text-center text-xs text-slate-400">Sin resultados</li>
            ) : (
              filtered.map((u) => (
                <li key={u.id} onClick={() => { onChange(u.correo_tecnico); setSearch(""); setOpen(false); }}
                  className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition hover:bg-slate-50 ${u.correo_tecnico === value ? "font-medium text-slate-900" : "text-slate-700"}`}>
                  <span className="truncate">{u.nombre_tecnico}</span>
                  <span className="ml-auto shrink-0 text-xs text-slate-400">{u.sucursal}</span>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function ApartarTecnicoSinTicket() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [tecnico, setTecnico] = useState("");
  const [correoTecnico, setCorreoTecnico] = useState("");
  const [cliente, setCliente] = useState("");
  const [equipo, setEquipo] = useState("");
  const [localidad, setLocalidad] = useState("");
  const [ciudad, setCiudad] = useState("");
  const [tecnicos, setTecnicos] = useState([]);

  const [selectedActivities, setSelectedActivities] = useState({
    Instalacion: false, Capacitacion: false, Diagnostico: false,
    Mantenimiento: false, "Servicio Correctivo": false, Garantia: false,
    Expo: false, "Actividad Interna": false
  });

  const [range, setRange] = useState([{ startDate: new Date(), endDate: new Date(), key: "selection" }]);
  const [daysList, setDaysList] = useState([]);
  const [sameHours, setSameHours] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const startDate = useMemo(() => range[0]?.startDate ?? new Date(), [range]);
  const endDate = useMemo(() => range[0]?.endDate ?? new Date(), [range]);

  const formatMX = (d) => d.toLocaleDateString("es-MX", { weekday: "short", month: "short", day: "numeric" });

  useEffect(() => {
    axios.get("https://desarrollotecnologicoar.com/api5/tecnicos")
      .then((res) => setTecnicos(res.data))
      .catch((err) => console.error("Error al obtener tecnicos:", err));
  }, []);

  const generateDaysFromRange = (s = startDate, e = endDate) => {
    const start = new Date(s); const end = new Date(e);
    start.setHours(0, 0, 0, 0); end.setHours(0, 0, 0, 0);
    const out = []; const cur = new Date(start);
    while (cur <= end) { out.push({ date: new Date(cur), entrada: "", salida: "" }); cur.setDate(cur.getDate() + 1); }
    setDaysList(out);
  };

  const handleHourChange = (index, field, value) => {
    setDaysList((prev) => {
      const copy = [...prev]; copy[index] = { ...copy[index], [field]: value };
      if (sameHours) { const v = copy[index][field]; return copy.map((d) => ({ ...d, [field]: v })); }
      return copy;
    });
  };

  const handleSameHoursChange = (e) => {
    const checked = e.target.checked; setSameHours(checked);
    if (checked && daysList.length) { const first = daysList[0]; setDaysList(daysList.map((d) => ({ ...d, entrada: first.entrada, salida: first.salida }))); }
  };

  const handleRemoveDay = (index) => { setDaysList((prev) => prev.filter((_, i) => i !== index)); };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const now = new Date(); const oneHourAhead = new Date(now.getTime() + 60 * 60 * 1000);
    for (const day of daysList) {
      if (day.date.toDateString() === now.toDateString() && day.entrada) {
        const [h, m] = day.entrada.split(":").map(Number);
        const scheduled = new Date(day.date); scheduled.setHours(h, m, 0, 0);
        if (scheduled < oneHourAhead) { alert("La hora de entrada debe programarse con al menos 1 hora de antelacion."); setIsSubmitting(false); return; }
      }
    }
    if (!tecnico.trim() && isAdmin) { alert("Selecciona un tecnico."); setIsSubmitting(false); return; }
    if (!cliente.trim()) { alert("Indica el nombre del cliente."); setIsSubmitting(false); return; }
    if (!isAdmin && !equipo.trim()) { alert("Indica el equipo/maquina a visitar."); setIsSubmitting(false); return; }
    if (!isAdmin && !localidad) { alert("Indica si el servicio es local o foraneo."); setIsSubmitting(false); return; }
    if (!isAdmin && !ciudad.trim()) { alert("Indica la ciudad del servicio."); setIsSubmitting(false); return; }
    if (daysList.length === 0) { alert("Agrega al menos un dia con horario."); setIsSubmitting(false); return; }
    const diasSinHorario = daysList.filter((d) => !d.entrada || !d.salida);
    if (diasSinHorario.length > 0) { alert("Todos los dias deben tener hora de entrada y salida definidas."); setIsSubmitting(false); return; }

    const primerDia = daysList[0];
    const payload = {
      ticketid: null, empresa: cliente, contacto: cliente, equipo: equipo.trim() || "No aplica",
      numero_serie: "", ticket: "SIN_TICKET", fecha_reserva: primerDia.date.toISOString(),
      hora_reserva: primerDia.entrada || "09:00:00",
      status: "Apartado",
      correo_cliente: "no_definido@correo.com",
      correo_tecnico: isAdmin ? correoTecnico : "",
      domicilio: "Sin especificar", ciudad: ciudad.trim() || "Guadalajara", telefono: "",
      localidad: localidad || "Local", nombre_agente: user.displayName, correo_agente: user.email,
    };

    try {
      const response = await axios.post("https://desarrollotecnologicoar.com/api5/crear_reserva", payload, { headers: { "Content-Type": "application/json" } });
      const reservaId = response.data.reserva.id;
      const itinerarioData = { reserva_id: reservaId, itinerario: daysList.map((d) => ({ fecha: d.date.toISOString().split("T")[0], entrada: d.entrada, salida: d.salida })) };
      await axios.post("https://desarrollotecnologicoar.com/api5/itinerario_ticket", itinerarioData);
      const actividadesSeleccionadas = Object.entries(selectedActivities).filter(([_, selected]) => selected).map(([actividad]) => actividad);
      if (actividadesSeleccionadas.length > 0) {
        const actividadesData = { actividades_list: actividadesSeleccionadas.map((actividad) => ({ reserva_id: reservaId.toString(), nombre_actividad: actividad })) };
        await axios.post("https://desarrollotecnologicoar.com/api5/guardar_actividades", actividadesData.actividades_list);
      }
      alert("Apartado, itinerario y actividades guardados correctamente.");
      navigate("/actividad");
    } catch (error) {
      const mensajeError = error.response?.data?.error || error.message;
      if (mensajeError.includes("Ya existe una reserva para este ticket")) { alert("Este tecnico ya tiene un apartado registrado."); }
      else { alert("Error al crear el apartado."); }
      console.error("Error al crear el apartado:", mensajeError);
    } finally { setIsSubmitting(false); }
  };

  return (
    <div className="page-container">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left - Main content */}
        <div className="lg:col-span-2 space-y-5">
          <h1 className="page-title">Apartar tecnico (sin ticket)</h1>

          {/* Basic data */}
          <div className="card p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {isAdmin && (
                <div>
                  <SearchableUserSelect
                    users={tecnicos}
                    value={correoTecnico}
                    onChange={(correo) => {
                      const selected = tecnicos.find((t) => t.correo_tecnico === correo);
                      setTecnico(selected?.nombre_tecnico || "");
                      setCorreoTecnico(correo);
                    }}
                    label="Tecnico"
                    placeholder="Buscar tecnico..."
                  />
                </div>
              )}
              {!isAdmin && (
                <div className="sm:col-span-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                  <span className="font-semibold">Solicitud de apartado:</span> Un coordinador revisará tu solicitud y asignará un técnico disponible.
                </div>
              )}
              <div>
                <label className="label">Nombre del cliente</label>
                <input type="text" value={cliente} onChange={(e) => setCliente(e.target.value)} className="input" />
              </div>
              {!isAdmin && (
                <>
                  <div>
                    <label className="label">Equipo/Maquina <span className="text-rose-500">*</span></label>
                    <input type="text" value={equipo} onChange={(e) => setEquipo(e.target.value)} className="input" placeholder="CNC SHOP PRO" />
                  </div>
                  <div>
                    <label className="label">Local o foraneo <span className="text-rose-500">*</span></label>
                    <select value={localidad} onChange={(e) => setLocalidad(e.target.value)} className="input">
                      <option value="">Selecciona...</option>
                      <option value="Local">Local</option>
                      <option value="Foraneo">Foraneo</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">Ciudad <span className="text-rose-500">*</span></label>
                    <input type="text" value={ciudad} onChange={(e) => setCiudad(e.target.value)} className="input" placeholder="Ej. Guadalajara" />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Activities */}
          <div className="card p-5">
            <p className="font-semibold text-slate-900 mb-3">Selecciona las actividades</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.keys(selectedActivities).map((actividad) => (
                <label key={actividad} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                  <input type="checkbox" checked={selectedActivities[actividad]} onChange={(e) => setSelectedActivities({ ...selectedActivities, [actividad]: e.target.checked })} className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500" />
                  {actividad}
                </label>
              ))}
            </div>
          </div>

          {/* Itinerary hours */}
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-3">
              <HiOutlineClock className="w-5 h-5 text-slate-500" />
              <p className="font-semibold text-slate-900">Hora de entrada y salida</p>
            </div>
            <hr className="border-slate-200 mb-3" />
            <label className="flex items-center gap-2 mb-4 cursor-pointer text-sm text-slate-700">
              <input type="checkbox" checked={sameHours} onChange={handleSameHoursChange} className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500" />
              Usar el mismo horario para todos los dias
            </label>
            <div className="space-y-2">
              {daysList.map((day, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg">
                  <span className="flex-1 text-sm text-slate-800 capitalize">
                    {day.date.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "short" })}
                  </span>
                  <input type="time" value={day.entrada} onChange={(e) => handleHourChange(idx, "entrada", e.target.value)} className="input w-32 py-1.5 text-sm" />
                  <input type="time" value={day.salida} onChange={(e) => handleHourChange(idx, "salida", e.target.value)} className="input w-32 py-1.5 text-sm" />
                  <button onClick={() => handleRemoveDay(idx)} className="p-1.5 text-slate-400 hover:text-red-600 transition-colors">
                    <HiOutlineTrash className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {daysList.length === 0 && (
                <div className="empty-state py-8">
                  <p className="text-sm text-slate-500">Selecciona fechas en el calendario para generar el itinerario.</p>
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3">
            <button onClick={handleSubmit} disabled={isSubmitting || daysList.length === 0} className="btn-primary text-sm">
              {isSubmitting ? "Guardando..." : "Apartar tecnico"}
            </button>
            <button onClick={() => window.history.back()} className="btn-secondary text-sm">Cancelar</button>
          </div>
        </div>

        {/* Right - Calendar */}
        <div className="lg:col-span-1">
          <div className="sticky top-20 rounded-card bg-slate-800 text-white p-5 shadow-card text-center">
            <div className="flex items-center justify-center gap-2 mb-3">
              <HiOutlineCalendarDays className="w-5 h-5" />
              <p className="font-semibold">Selecciona fechas</p>
            </div>
            <hr className="border-slate-600 mb-4" />
            <DateRange
              ranges={range}
              onChange={({ selection }) => { setRange([selection]); generateDaysFromRange(selection.startDate, selection.endDate); }}
              moveRangeOnFirstSelection={false}
              locale={esLocale}
              rangeColors={["#0f172a"]}
              months={1}
              direction="vertical"
              showMonthAndYearPickers
              weekStartsOn={1}
            />
            <p className="text-sm mt-3 opacity-80">{`${formatMX(startDate)} — ${formatMX(endDate)}`}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
